/**
 * The Sources panel (docs/plans/lms-course-builder-plan.md 8.3, 5.3, G17,
 * R4, 18.2 Q7, 18.3, 18.4; WP-B31): inside VersionEditor, for a draft the
 * course builder wrote, a collapsible panel that lists, for the chosen part of
 * the course (one section, or the course-wide fields), every element the
 * builder wrote with where it came from: the source file (or web link, shown
 * with its address), the page or heading, and the matched text.
 *
 * - The panel reads GET /admin/builder/versions/:id/provenance ONCE (the
 *   newest row per element) and filters by part in the browser.
 * - A verified element says "Verified"; an element with no source, or whose
 *   quote the code could not find in the source, says "Unsourced: check this"
 *   (G17: the element is kept and flagged, never hidden).
 * - The stored quote is the normalised text the code matched (whitespace
 *   collapsed, curly quotes and dashes folded; 18.4), so it is labelled
 *   "Matched text", not presented as the verbatim span.
 * - A withdrawn source (9.4) says "Withdrawn" and is not opened. Opening a
 *   file uses openPendingTab (the tab opens inside the click, then points at
 *   the presigned URL; a blocked tab gets a visible link instead).
 * - An element the draft no longer has (a slide or paragraph removed by hand)
 *   is listed as "No longer in the draft", so nothing silently disappears.
 * - A fact an admin stated in an Edit with AI message (the course editor,
 *   docs/plans/lms-course-editor-plan.md 8.7, 5.3, G23; WP-E31) is an
 *   instruction row: no source, verified, locator kind 'instruction'. It says
 *   "Stated in Edit with AI" (neither "Verified" nor "Unsourced") and "Stated
 *   by <name> in message n on <date>" with the matched text, the name and the
 *   number resolved through the version's messages (GET .../edits, 18.5), and
 *   a link that opens the Edit with AI panel at that message. It is not
 *   counted as one to check.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FileText, Loader2, MessageSquareText } from 'lucide-react';
import { fieldCls, outlineBtnCls } from '../../../../../ui/styles';
import { useToast } from '../../../../../ui/Toast';
import { getBuilderSourceUrl, getVersionProvenance, readBuilderError } from '../../../../api/builder';
import type { BuilderSource, ProvenanceItem } from '../../../../api/builder';
import type { EditorQuestion } from '../../../../api/catalog';
import type { EditMessage } from '../../../../api/editor';
import type { CourseContent, CourseSection, Slide } from '../../../../content/types';
import { formatDay, openPendingTab } from '../../../../components/player/DoneStage';
import { noticeCls, pillCls } from '../../../../components/player/styles';
import { cardCls, hintCls, labelCls, linkCls, microLabelCls } from '../styles';

export interface SourcesPanelProps {
  versionId: string;
  content: CourseContent;
  questions: EditorQuestion[];
  /** The part shown: a section id, or null for the course-wide fields. */
  sectionId: string | null;
  onSectionChange: (sectionId: string | null) => void;
  /** The builder run's sources (for a link source's address and a file's public URL); optional. */
  sources?: BuilderSource[] | null;
  /** Start expanded (default collapsed). */
  defaultOpen?: boolean;
  /** The version's Edit with AI messages (editor plan 8.7): an instruction row names its message's author and number from them. */
  messages?: ReadonlyArray<Pick<EditMessage, 'id' | 'ordinal' | 'requested_by' | 'created_at'>> | null;
  /** Open the Edit with AI panel at a message (editor plan 8.7); without it the row shows no link. */
  onShowMessage?: (messageId: string) => void;
}

/** Editor plan 5.3, Appendix D: the locator of a fact stated in an Edit with AI message. */
export interface InstructionLocator {
  kind: 'instruction';
  message_id: string | null;
  actor_id: string | null;
  at: string | null;
  quote: string | null;
}

/** One row of the panel: an element of the draft and its provenance item (or none). */
export interface SourceRow {
  key: string;
  label: string;
  excerpt: string;
  item: ProvenanceItem | null;
  /** false when the provenance names an element the draft no longer has. */
  present: boolean;
}

const COURSE_WIDE = '__course__';

/** The reading's paragraphs, split exactly as the backend numbers them (courses/diff.js readingParagraphs). */
export function readingParagraphs(md: string | null | undefined): string[] {
  if (typeof md !== 'string' || !md.trim()) return [];
  return md
    .split(/\r?\n[ \t]*\r?\n/)
    .map((block) => block.split(/\r?\n/).map((line) => line.replace(/\s+$/, '')).join('\n').trim())
    .filter((block) => block !== '');
}

function shorten(text: string | null | undefined, max = 90): string {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 3)}...` : t;
}

function slideText(slide: Slide): string {
  const s = slide as unknown as Record<string, unknown>;
  for (const key of ['heading', 'stem', 'question', 'body_md', 'caption']) {
    if (typeof s[key] === 'string' && (s[key] as string).trim()) return s[key] as string;
  }
  return '';
}

function keyOf(kind: string, ref: string): string {
  return `${kind}|${ref}`;
}

/** The rows for one section: the section itself, its slides, its reading paragraphs and its questions. */
export function sectionRows(section: CourseSection, questions: EditorQuestion[], items: ProvenanceItem[]): SourceRow[] {
  const byKey = new Map(items.map((i) => [keyOf(i.element_kind, i.element_ref), i]));
  const used = new Set<string>();
  const take = (kind: string, ref: string): ProvenanceItem | null => {
    const k = keyOf(kind, ref);
    const found = byKey.get(k) ?? null;
    if (found) used.add(k);
    return found;
  };
  const rows: SourceRow[] = [];
  const whole = take('section', section.id);
  if (whole) rows.push({ key: keyOf('section', section.id), label: 'The section as a whole', excerpt: section.title || '', item: whole, present: true });

  const slides = Array.isArray(section.slides) ? section.slides : [];
  slides.forEach((slide, i) => {
    const item = take('slide', slide.id);
    if (item) rows.push({ key: keyOf('slide', slide.id), label: `Slide ${i + 1}`, excerpt: shorten(slideText(slide)), item, present: true });
  });
  const paragraphs = readingParagraphs(section.reading_md);
  paragraphs.forEach((text, i) => {
    const ref = `${section.id}:p${i + 1}`;
    const item = take('reading_paragraph', ref);
    if (item) rows.push({ key: keyOf('reading_paragraph', ref), label: `Reading paragraph ${i + 1}`, excerpt: shorten(text), item, present: true });
  });
  const own = questions.filter((q) => q.section_id === section.id);
  own.forEach((q) => {
    const ref = `q${q.ordinal}`;
    const item = take('question', ref);
    if (item) rows.push({ key: keyOf('question', ref), label: `Question ${q.ordinal}`, excerpt: shorten(q.stem), item, present: true });
  });

  // Provenance for elements of this section the draft no longer has.
  const slidePrefix = `${section.id}-`;
  const paraPrefix = `${section.id}:p`;
  items.forEach((i) => {
    const k = keyOf(i.element_kind, i.element_ref);
    if (used.has(k)) return;
    const mine =
      (i.element_kind === 'slide' && i.element_ref.startsWith(slidePrefix)) ||
      (i.element_kind === 'reading_paragraph' && i.element_ref.startsWith(paraPrefix));
    if (!mine) return;
    const label = i.element_kind === 'slide' ? `Slide ${i.element_ref}` : `Reading paragraph ${i.element_ref.slice(paraPrefix.length)}`;
    rows.push({ key: k, label, excerpt: '', item: i, present: false });
  });
  return rows;
}

/** The rows for the course-wide fields: objectives, takeaways, glossary, and the not-covered line. */
export function courseWideRows(content: CourseContent, items: ProvenanceItem[]): SourceRow[] {
  const byKey = new Map(items.map((i) => [keyOf(i.element_kind, i.element_ref), i]));
  const rows: SourceRow[] = [];
  (content.learning_objectives || []).forEach((text, i) => {
    const item = byKey.get(keyOf('objective', `o${i}`));
    if (item) rows.push({ key: keyOf('objective', `o${i}`), label: `Objective ${i + 1}`, excerpt: shorten(text), item, present: true });
  });
  (content.key_takeaways || []).forEach((text, i) => {
    const item = byKey.get(keyOf('takeaway', `t${i}`));
    if (item) rows.push({ key: keyOf('takeaway', `t${i}`), label: `Takeaway ${i + 1}`, excerpt: shorten(text), item, present: true });
  });
  (content.glossary || []).forEach((g) => {
    const ref = `g:${(g.term || '').trim()}`;
    const item = byKey.get(keyOf('glossary', ref));
    if (item) rows.push({ key: keyOf('glossary', ref), label: `Glossary: ${g.term}`, excerpt: shorten(g.definition), item, present: true });
  });
  const nc = byKey.get(keyOf('not_covered', 'not_covered'));
  if (nc) rows.push({ key: keyOf('not_covered', 'not_covered'), label: 'What the course does not cover', excerpt: shorten(content.not_covered), item: nc, present: true });
  return rows;
}

/** Where in the source: "page 4", "3 Consent > 3.2 Elements", or "part 7". */
export function locationText(item: ProvenanceItem): string {
  const loc = item.locator || {};
  const parts: string[] = [];
  if (typeof loc.page === 'number') parts.push(`page ${loc.page}`);
  if (Array.isArray(loc.heading_path) && loc.heading_path.length > 0) parts.push(loc.heading_path.join(' > '));
  if (parts.length === 0 && typeof loc.unit === 'number') parts.push(`part ${loc.unit}`);
  return parts.join(', ');
}

/** True when the element counts as sourced (a source and a quote the code verified). */
export function isVerified(item: ProvenanceItem | null): boolean {
  return Boolean(item && item.verified && item.source);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Editor plan 5.3: an instruction row is (source NULL, verified true, locator
 * kind 'instruction'); its locator, or null for every other row (a source row,
 * or an unsourced row: source NULL and verified false).
 */
export function instructionLocatorOf(item: ProvenanceItem | null): InstructionLocator | null {
  if (!item || item.source || item.verified !== true) return null;
  const loc = (item.locator || {}) as Record<string, unknown>;
  if (loc.kind !== 'instruction') return null;
  return {
    kind: 'instruction',
    message_id: stringOrNull(loc.message_id),
    actor_id: stringOrNull(loc.actor_id),
    at: stringOrNull(loc.at),
    quote: stringOrNull(loc.quote),
  };
}

/** 8.7: "Stated by <name> in message n on <date>", from the message when the list holds it. */
export function statedLine(
  locator: InstructionLocator,
  message: Pick<EditMessage, 'ordinal' | 'requested_by' | 'created_at'> | null | undefined
): string {
  const day = formatDay(locator.at || message?.created_at || null);
  const on = day ? ` on ${day}` : '';
  if (!message) return `Stated in an Edit with AI message${on}`;
  const name = message.requested_by?.full_name && message.requested_by.full_name.trim() ? message.requested_by.full_name.trim() : 'an admin';
  return `Stated by ${name} in message ${message.ordinal}${on}`;
}

function StatusWord({ item }: { item: ProvenanceItem | null }) {
  if (instructionLocatorOf(item)) {
    return (
      <span className={pillCls('primary')} data-testid="lms-sources-status" data-status="instruction">
        <MessageSquareText className="h-3.5 w-3.5" aria-hidden="true" />
        Stated in Edit with AI
      </span>
    );
  }
  if (item?.source?.withdrawn) {
    return (
      <span className={pillCls('gray')} data-testid="lms-sources-status" data-status="withdrawn">
        Withdrawn
      </span>
    );
  }
  if (isVerified(item)) {
    return (
      <span className={pillCls('green')} data-testid="lms-sources-status" data-status="verified">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
        Verified
      </span>
    );
  }
  return (
    <span className={pillCls('amber')} data-testid="lms-sources-status" data-status="unsourced">
      Unsourced: check this
    </span>
  );
}

export function SourcesPanel({
  versionId,
  content,
  questions,
  sectionId,
  onSectionChange,
  sources,
  defaultOpen = false,
  messages,
  onShowMessage,
}: SourcesPanelProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(defaultOpen);
  const [items, setItems] = useState<ProvenanceItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<{ id: string; url: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    getVersionProvenance(versionId)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        if (!cancelled) setError(readBuilderError(err, 'The sources could not be loaded.').message);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId]);

  const sections = useMemo(() => (Array.isArray(content.sections) ? content.sections : []), [content.sections]);
  const section = sectionId ? sections.find((s) => s.id === sectionId) ?? null : null;
  const publicUrls = useMemo(() => {
    const map = new Map<string, string>();
    (sources || []).forEach((s) => {
      if (s.public_url) map.set(s.id, s.public_url);
    });
    return map;
  }, [sources]);

  const all = items ?? [];
  // Editor plan 8.7: a stated fact is neither verified nor unsourced, so it is not one to check.
  const toCheck = all.filter((i) => !isVerified(i) && !instructionLocatorOf(i)).length;
  const hasStated = all.some((i) => instructionLocatorOf(i) !== null);
  const messageOf = (messageId: string | null) => (messageId ? (messages || []).find((m) => m.id === messageId) ?? null : null);
  const rows = section ? sectionRows(section, questions, all) : courseWideRows(content, all);

  const openSource = async (id: string) => {
    setBlocked(null);
    // Before any await: the tab opens inside the click's user activation.
    const tab = openPendingTab();
    setOpening(id);
    try {
      const { url } = await getBuilderSourceUrl(id);
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlocked({ id, url });
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      const problem = readBuilderError(err, 'The file could not be opened.');
      toast(problem.status === 404 ? 'That file was withdrawn or is no longer available.' : problem.message, { type: 'error' });
    } finally {
      setOpening(null);
    }
  };

  const summary =
    items === null
      ? error
        ? 'not loaded'
        : 'loading'
      : `${all.length} element${all.length === 1 ? '' : 's'}, ${toCheck} to check`;

  return (
    <section className={`${cardCls} space-y-3`} data-testid="lms-sources-panel" aria-label="Sources">
      <button
        type="button"
        className="flex min-h-[44px] w-full items-center gap-2 rounded-lg text-left text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 sm:min-h-0"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="lms-sources-toggle"
      >
        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        Sources
        <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-testid="lms-sources-summary">
          {`(${summary})`}
        </span>
      </button>

      {open ? (
        <div className="space-y-3">
          <p className={hintCls}>
            Where the builder took each element from. The matched text is the passage the builder found in the source, with spacing and quote marks
            normalised. An element marked "Unsourced: check this" has no passage the builder could confirm: check it against the source before publishing.
            {hasStated
              ? ' An element marked "Stated in Edit with AI" carries a fact an admin stated in an Edit with AI message; its matched text is from that message.'
              : ''}
          </p>
          <div>
            <label className={labelCls} htmlFor={`lms-sources-part-${versionId}`}>
              Part of the course
            </label>
            <select
              id={`lms-sources-part-${versionId}`}
              className={`${fieldCls} min-h-[44px] w-full sm:min-h-0 sm:w-80`}
              value={section ? section.id : COURSE_WIDE}
              onChange={(e) => onSectionChange(e.target.value === COURSE_WIDE ? null : e.target.value)}
              data-testid="lms-sources-part"
            >
              <option value={COURSE_WIDE}>Course: objectives, takeaways, glossary</option>
              {sections.map((s, i) => (
                <option key={s.id} value={s.id}>{`Section ${i + 1}: ${s.title || s.id}`}</option>
              ))}
            </select>
          </div>

          {items === null && !error ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" role="status">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Loading the sources...
            </div>
          ) : null}
          {error ? (
            <div className={noticeCls('red')} role="alert" data-testid="lms-sources-error">
              {error}
            </div>
          ) : null}
          {items !== null && rows.length === 0 ? (
            <p className={hintCls} data-testid="lms-sources-empty">
              The builder recorded no sources for this part of the course.
            </p>
          ) : null}

          {rows.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-sources-list">
              {rows.map((row) => {
                const item = row.item;
                const source = item?.source ?? null;
                const where = item ? locationText(item) : '';
                const quote = item?.locator?.quote ? String(item.locator.quote) : '';
                const address = source ? publicUrls.get(source.id) ?? null : null;
                const stated = instructionLocatorOf(item);
                const statedMessage = stated ? messageOf(stated.message_id) : null;
                return (
                  <li key={row.key} className="space-y-1.5 py-2.5" data-testid="lms-sources-row" data-ref={item?.element_ref ?? ''}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.label}</span>
                      <StatusWord item={item} />
                      {!row.present ? (
                        <span className={pillCls('gray')} data-testid="lms-sources-gone">
                          No longer in the draft
                        </span>
                      ) : null}
                    </div>
                    {row.excerpt ? <p className="text-xs text-gray-600 dark:text-gray-300">{row.excerpt}</p> : null}
                    {source ? (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-medium" data-testid="lms-sources-file">
                          {source.title || source.filename}
                        </span>
                        {where ? <span data-testid="lms-sources-where">{where}</span> : null}
                        {address ? (
                          <a
                            href={address}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${linkCls} inline-flex items-center gap-1 break-all`}
                            data-testid="lms-sources-address"
                          >
                            {address}
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </a>
                        ) : null}
                        {!source.withdrawn ? (
                          <button
                            type="button"
                            className={`${outlineBtnCls()} text-xs`}
                            onClick={() => void openSource(source.id)}
                            disabled={opening === source.id}
                            data-testid="lms-sources-open"
                          >
                            {opening === source.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                            Open the file
                          </button>
                        ) : null}
                        {blocked && blocked.id === source.id ? (
                          <a href={blocked.url} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="lms-sources-blocked-link">
                            The browser blocked the new tab: open the file here
                          </a>
                        ) : null}
                      </div>
                    ) : stated ? (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-medium" data-testid="lms-sources-stated">
                          {statedLine(stated, statedMessage)}
                        </span>
                        {onShowMessage && stated.message_id ? (
                          <button
                            type="button"
                            className={`${linkCls} inline-flex items-center gap-1 text-xs`}
                            onClick={() => onShowMessage(stated.message_id as string)}
                            data-testid="lms-sources-show-message"
                          >
                            <MessageSquareText className="h-3.5 w-3.5" aria-hidden="true" />
                            {statedMessage ? `Show message ${statedMessage.ordinal}` : 'Show the message'}
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">No source file is recorded for this element.</p>
                    )}
                    {quote ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                        <div className={microLabelCls}>Matched text</div>
                        <p className="whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300" data-testid="lms-sources-quote">
                          {quote}
                        </p>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default SourcesPanel;
